<template>
  <v-dialog v-model="$parent.formTypesDialog" fullscreen transition="dialog-bottom-transition">
    <v-card>
      <!-- Header -->
      <v-toolbar dark color="primary">
        <v-toolbar-title>Select Form Types that trigger the alert</v-toolbar-title>
        <div class="flex-grow-1" />
        <v-toolbar-items>
          <v-btn text dark class="ma-0" @click="$parent.formTypesDialog = false"> Close </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <!-- Body -->
      <v-card-text>
        <v-radio-group v-model="alertCriteria.settings.form_type_selection" mandatory>
          <v-radio value="all" label="Receive an alert for all form types" />
          <v-radio value="individual" label="Choose individual form types" />
        </v-radio-group>

        <template v-if="alertCriteria.settings.form_type_selection === 'individual'">
          <helpers-section-header class="mt-0">
            Choose from commonly selected forms
          </helpers-section-header>
          <v-checkbox
            :model-value="isChecked([21])"
            :indeterminate="isIndetermined([21])"
            label="13F Filings"
            hint="13Fs are holdings reports filed quarterly by managers with investment discretion of over $100 million"
            persistent-hint
            color="primary"
            @update:model-value="toggle($event, [21])"
          />
          <v-checkbox
            :model-value="isChecked([185, 187])"
            :indeterminate="isIndetermined([185, 187])"
            label="13D/G Filings"
            hint="13D/G filings are generally submitted within 10 days by anyone who acquires ownership of more than 5% of any class of publicly traded securities"
            persistent-hint
            color="primary"
            @update:model-value="toggle($event, [185, 187])"
          />
          <v-checkbox
            :model-value="isChecked([8, 9, 10])"
            :indeterminate="isIndetermined([8, 9, 10])"
            label="Insider Transactions (3, 4, 5)"
            hint="Receive alerts when directors, officers, or owners of more than 10% of a security file a report on their ownership status. Form 3 is the initial filing. Changes are reported on Form 4 and Form 5 is the annual statement of ownership."
            persistent-hint
            color="primary"
            @update:model-value="toggle($event, [8, 9, 10])"
          />
          <v-checkbox
            :model-value="isChecked([47])"
            :indeterminate="isIndetermined([47])"
            label="Form D"
            hint="Companies may use an exemption under Regulation D to offer and sell securities without having to register the offering with the SEC."
            persistent-hint
            color="primary"
            @update:model-value="toggle($event, [47])"
          />
          <v-checkbox
            :model-value="isChecked([13])"
            :indeterminate="isIndetermined([13])"
            label="Form 8-K"
            hint="Public companies use Form 8-K to notify investors of specific events that may be important to shareholders or to the SEC. If you add Form 8-K filings to your e-mail alert you have the option of specifying which events you would like to be notified about."
            persistent-hint
            color="primary"
            @update:model-value="toggle($event, [13])"
          />

          <!-- Autocomplete selected forms -->
          <div class="my-12">
            <helpers-section-sub-header>Selected forms</helpers-section-sub-header>

            <v-alert
              v-if="alertCriteria.form_types.length === 0"
              :value="true"
              type="error"
              class="d-inline-flex my-2"
            >
              You must select at least one form type!
            </v-alert>

            <v-row>
              <v-col cols="12" md="10" lg="8" xl="6">
                <v-autocomplete
                  v-model="alertCriteria.form_types"
                  :items="secFilingTypes"
                  return-object
                  item-title="name"
                  item-value="id"
                  placeholder="No selections made yet"
                  hint="Type in the box to search available SEC filing types"
                  persistent-hint
                  multiple
                  chips
                  closable-chips
                  density="compact"
                  variant="outlined"
                  clearable
                />
              </v-col>
            </v-row>
          </div>

          <!-- Form8k selected -->
          <template v-if="Form8kSelected">
            <v-slide-x-transition appear>
              <div class="my-12">
                <helpers-section-sub-header
                  >Optionally restrict which 8K events you want to be notified
                  about</helpers-section-sub-header
                >
                <p>Leaving all events blank will bring back all 8K events.</p>

                <v-checkbox
                  v-for="form8kEvent in form8kEventTypes"
                  :key="form8kEvent.id"
                  :model-value="
                    alertCriteria.form8k_events.some(event => event.id === form8kEvent.id)
                  "
                  :label="`${form8kEvent.item_number}: ${form8kEvent.description}`"
                  hide-details
                  class="mt-1"
                  @update:model-value="toggleForm8kEvent($event, form8kEvent)"
                />
              </div>
            </v-slide-x-transition>
          </template>

          <!-- FormD selected -->
          <template v-if="FormDSelected">
            <v-slide-x-transition appear>
              <v-row>
                <v-col cols="12" md="4">
                  <v-card variant="outlined">
                    <v-card-title>
                      <helpers-section-sub-header
                        >Optionally restrict to new or amended filings
                        only</helpers-section-sub-header
                      >
                    </v-card-title>
                    <v-card-subtitle
                      >Leaving all groups blank will bring back all Form D filings.</v-card-subtitle
                    >
                    <v-card-text style="max-height: 500px; overflow-y: auto">
                      <v-radio-group v-model="alertCriteria.form_type_restriction">
                        <v-radio label="Any (original or amended)" :value="0"></v-radio>
                        <v-radio label="Original (new) filings only" :value="1"></v-radio>
                        <v-radio label="Amended filings only" :value="2"></v-radio>
                      </v-radio-group>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card variant="outlined">
                    <v-card-title>
                      <helpers-section-sub-header
                        >Optionally restrict to Form D filings with the following Industry
                        Groups</helpers-section-sub-header
                      >
                    </v-card-title>
                    <v-card-subtitle
                      >Leaving all groups blank will bring back all Form D filings.</v-card-subtitle
                    >
                    <v-card-text style="max-height: 500px; overflow-y: auto">
                      <div>
                        <v-checkbox
                          v-for="devent in formDIndustryGroups"
                          :key="devent.id"
                          :model-value="
                            alertCriteria.formd_industry_groups.some(
                              event => event.id === devent.id
                            )
                          "
                          :label="`${devent.description}`"
                          hide-details
                          class="mt-1"
                          @update:model-value="toggleFormDIndustryGroup($event, devent)"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card variant="outlined">
                    <v-card-title>
                      <helpers-section-sub-header
                        >Optionally restrict to Form D filings with the following Types of
                        Securities</helpers-section-sub-header
                      >
                    </v-card-title>
                    <v-card-subtitle
                      >Leaving all security types blank will bring back all Form D
                      filings.</v-card-subtitle
                    >
                    <v-card-text style="max-height: 500px; overflow-y: auto">
                      <v-checkbox
                        v-for="devent in formDSecurities"
                        :key="devent.id"
                        :model-value="
                          alertCriteria.formd_securities.some(event => event.id === devent.id)
                        "
                        :label="`${devent.description}`"
                        hide-details
                        class="mt-1"
                        @update:model-value="toggleFormDSecurities($event, devent)"
                      />
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card variant="outlined">
                    <v-card-title>
                      <helpers-section-sub-header
                        >Optionally restrict to Form D filings with the following Investment Fund
                        Types</helpers-section-sub-header
                      >
                    </v-card-title>
                    <v-card-subtitle style="white-space: wrap;"
                      >Leaving all investment fund types blank will bring back all Form D
                      filings.</v-card-subtitle
                    >
                    <v-card-text style="max-height: 500px; overflow-y: auto">
                      <v-checkbox
                        v-for="devent in formDInvestmentFunds"
                        :key="devent.id"
                        :model-value="
                          alertCriteria.formd_investment_funds.some(event => event.id === devent.id)
                        "
                        :label="`${devent.description}`"
                        hide-details
                        class="mt-1"
                        @update:model-value="toggleFormDInvestmentFunds($event, devent)"
                      />
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col v-if="formd_states_loaded" cols="12" md="4">
                  <v-card variant="outlined">
                    <v-card-title>
                      <helpers-section-sub-header
                        >Optionally restrict to Form D Filings From Specific
                        States</helpers-section-sub-header
                      >
                    </v-card-title>
                    <v-card-subtitle style="white-space: wrap;"
                      >Leaving all states blank will bring back all Form D filings.</v-card-subtitle
                    >
                    <v-card-subtitle style="white-space: wrap;">
                      Abbreviations below are taken from the SEC State and Country codes. The list
                      of codes
                      <a
                        href="https://www.sec.gov/edgar/searchedgar/edgarstatecodes"
                        target="_blank"
                        >can be found here</a
                      >
                    </v-card-subtitle>
                    <v-card-text style="max-height: 500px; overflow-y: auto">
                      <v-row density="compact">
                        <v-col cols="12" md="6" class="ma-0 pa-0" v-for="devent in FormDStates"
                        :key="devent.id">
                          <v-checkbox
                        density="compact"
                        :model-value="
                          alertCriteria.formd_states.some(event => event.id === devent.id)
                        "
                        :label="`${devent.description}`"
                        hide-details
                        class="mt-1"
                        @update:model-value="toggleFormDStates($event, devent)"
                      />
                        </v-col>

                      </v-row>
                      
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-slide-x-transition>
          </template>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import { mapWritableState } from 'pinia'

export default {
  data() {
    return {
      formd_states_loaded: false,
      secFilingTypes: [],
      form8kEventTypes: [],

      formDIndustryGroups: [
        { id: 1, description: 'Commercial Banking' },
        { id: 2, description: 'Insurance' },
        { id: 3, description: 'Investing' },
        { id: 4, description: 'Investment Banking' },
        { id: 5, description: 'Pooled Investment Fund' },
        { id: 6, description: 'Other Banking & Financial Services' },
        { id: 7, description: 'Business Services' },
        { id: 8, description: 'Electric Utilities' },
        { id: 9, description: 'Energy Conservation' },
        { id: 10, description: 'Coal Mining' },
        { id: 11, description: 'Environmental Services' },
        { id: 12, description: 'Oil & Gas' },
        { id: 13, description: 'Other Energy' },
        { id: 14, description: 'Biotechnology' },
        { id: 15, description: 'Health Insurance' },
        { id: 16, description: 'Hospitals & Physicians' },
        { id: 17, description: 'Pharmaceuticals' },
        { id: 18, description: 'Other Health Care' },
        { id: 19, description: 'Commercial' },
        { id: 20, description: 'Construction' },
        { id: 21, description: 'REITS & Finance' },
        { id: 22, description: 'Residential' },
        { id: 23, description: 'Other Real Estate' },
        { id: 24, description: 'Retailing' },
        { id: 25, description: 'Restaurants' },
        { id: 26, description: 'Computers' },
        { id: 27, description: 'Telecommunications' },
        { id: 28, description: 'Other Technology' },
        { id: 29, description: 'Airlines & Airports' },
        { id: 30, description: 'Lodging & Conventions' },
        { id: 31, description: 'Tourism & Travel Services' },
        { id: 32, description: 'Other Travel' },
        { id: 33, description: 'Manufacturing' },
        { id: 34, description: 'Other' }
      ],
      formDSecurities: [
        { id: 1, description: 'Equity' },
        { id: 2, description: 'Debt' },
        { id: 3, description: 'Option, Warrant or Other Right to Acquire Another Security' },
        {
          id: 4,
          description:
            'Security to be Acquired Upon Exercise of Option, Warrant or Other Right to Acquire Security'
        },
        { id: 5, description: 'Pooled Investment Fund Interests' },
        { id: 6, description: 'Tenant-in-Common Securities' },
        { id: 7, description: 'Mineral Property Securities' },
        { id: 8, description: 'Other' }
      ],
      formDInvestmentFunds: [
        { id: 1, description: 'Hedge Fund' },
        { id: 2, description: 'Private Equity Fund' },
        { id: 3, description: 'Venture Capital Fund' },
        { id: 4, description: 'Other Investment Fund' }
      ]
    }
  },

  computed: {
    ...mapWritableState(useAppStore, ['alertCriteria']),

    Form8kSelected() {
      return this.alertCriteria.form_types.some(form => form.id === 13)
    },
    FormDSelected() {
      return this.alertCriteria.form_types.some(form => form.id === 47)
    },
    FormDStates() {
      var items = useWwStore().states.map(state => ({
        id: state.state,
        description: state.state
      }))

      return items
    }
  },

  watch: {
    'alertCriteria.settings.form_type_selection'(val) {
      if (val === 'all') {
        this.alertCriteria.form_types = []
        this.alertCriteria.form8k_events = []
        this.alertCriteria.formd_industry_groups = []
        this.alertCriteria.formd_securities = []
        this.alertCriteria.formd_investment_funds = []
        this.alertCriteria.formd_states = []
      }
    },

    Form8kSelected(isSelected) {
      if (!isSelected) this.alertCriteria.form8k_events = []
    },
    FormDSelected(isSelected) {
      if (!isSelected) {
        this.alertCriteria.formd_industry_groups = []
        this.alertCriteria.formd_securities = []
        this.alertCriteria.formd_investment_funds = []
        this.alertCriteria.formd_states = []
      }
    }
  },

  mounted() {
    Promise.all([
        useWwStore().loadStates()
      ])
      // Include all sectors by default
        .then(() => {
    this.formd_states_loaded = true
    //  })
    api
      .get('sec_filing_type?id=all')
      .then(response => {
        if (response.data) {
          this.secFilingTypes = response.data
        }
      })
      .catch(error => console.log(error))

    api
      .get('form8k_event_type')
      .then(response => {
        if (response.data) {
          this.form8kEventTypes = response.data
        }
      })
      .catch(error => console.log(error))
    })
    if (!this.alertCriteria.form_type_restriction) this.alertCriteria.form_type_restriction = 0
  },

  methods: {
    toggle(isEnabled, arrayOfIDs) {
      if (isEnabled) {
        arrayOfIDs.forEach(id => {
          !this.alertCriteria.form_types.some(form => form.id === id) &&
            this.alertCriteria.form_types.push(
              this.secFilingTypes.find(filingType => filingType.id === id)
            )
        })
      } else {
        this.alertCriteria.form_types = this.alertCriteria.form_types.filter(
          form => !arrayOfIDs.includes(form.id)
        )
      }
    },

    isChecked(arrayOfIDs) {
      return arrayOfIDs.every(id => this.alertCriteria.form_types.some(form => form.id === id))
    },

    isIndetermined(arrayOfIDs) {
      const some = this.alertCriteria.form_types.some(form => arrayOfIDs.some(id => form.id === id))
      const every = this.isChecked(arrayOfIDs)

      return some && !every
    },

    toggleForm8kEvent(isEnabled, form8kEvent) {
      if (isEnabled) {
        this.alertCriteria.form8k_events.push(form8kEvent)
      } else {
        this.alertCriteria.form8k_events = this.alertCriteria.form8k_events.filter(
          item => item.id !== form8kEvent.id
        )
      }
    },
    toggleFormDIndustryGroup(isEnabled, form8kEvent) {
      if (isEnabled) {
        this.alertCriteria.formd_industry_groups.push(form8kEvent)
      } else {
        this.alertCriteria.formd_industry_groups = this.alertCriteria.formd_industry_groups.filter(
          item => item.id !== form8kEvent.id
        )
      }
    },
    toggleFormDStates(isEnabled, form8kEvent) {
      if (isEnabled) {
        this.alertCriteria.formd_states.push(form8kEvent)
      } else {
        this.alertCriteria.formd_states = this.alertCriteria.formd_states.filter(
          item => item.id !== form8kEvent.id
        )
      }
    },
    toggleFormDSecurities(isEnabled, form8kEvent) {
      if (isEnabled) {
        this.alertCriteria.formd_securities.push(form8kEvent)
      } else {
        this.alertCriteria.formd_securities = this.alertCriteria.formd_securities.filter(
          item => item.id !== form8kEvent.id
        )
      }
    },
    toggleFormDInvestmentFunds(isEnabled, form8kEvent) {
      if (isEnabled) {
        this.alertCriteria.formd_investment_funds.push(form8kEvent)
      } else {
        this.alertCriteria.formd_investment_funds =
          this.alertCriteria.formd_investment_funds.filter(item => item.id !== form8kEvent.id)
      }
    }
  }
}
</script>
